import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import RiyalSymbol from '../components/RiyalSymbol';
import { paymentService } from '../services/paymentService';
import '../styles/Payment.css';

function PayRemaining() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5002'}/api/bookings/${bookingId}`);
        const data = await res.json();
        
        if (data.success && data.booking) {
          setBooking(data.booking);
        } else {
          setError('لم يتم العثور على الحجز');
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('حدث خطأ أثناء جلب بيانات الحجز');
      } finally {
        setLoading(false);
      }
    };
    
    if (bookingId) {
      fetchBooking();
    }
  }, [bookingId]);

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>جاري التحميل...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>{error}</div>;
  if (!booking) return null;

  const totalAmount = booking.finalAmount || booking.totalAmount;
  const paidAmount = booking.paidAmount || 0;
  const remainingAmount = totalAmount - paidAmount;

  if (remainingAmount <= 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>هذا الحجز مدفوع بالكامل!</h2>
        <button className="primary-btn" onClick={() => navigate('/')}>العودة للرئيسية</button>
      </div>
    );
  }

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      const geideaResult = await paymentService.createGeideaPayment(
        booking._id || booking.id,
        booking.email,
        booking.phone
      );

      if (geideaResult.success && geideaResult.paymentUrl) {
        window.location.href = geideaResult.paymentUrl;
      } else {
        alert('فشل في إنشاء عملية الدفع: ' + geideaResult.message);
        setIsProcessing(false);
      }
    } catch (err: any) {
      alert('خطأ: ' + err.message);
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div 
      className="payment-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <div className="page-title">
            دفع المبلغ المتبقي
          </div>
        </div>
      </nav>

      <div className="payment-container">
        <div className="booking-summary">
          <h2>ملخص الحجز</h2>
          <div className="summary-details">
            <div className="summary-item">
              <span className="label">رقم الحجز:</span>
              <span className="value">{booking.bookingCode}</span>
            </div>
            {booking.boothType && (
              <div className="summary-item">
                <span className="label">نوع الكابينة:</span>
                <span className="value">{booking.boothDisplayName || booking.boothType}</span>
              </div>
            )}
            <div className="summary-item">
              <span className="label">اسم العميل:</span>
              <span className="value">{booking.customerName}</span>
            </div>
            {booking.eventDate && (
              <div className="summary-item">
                <span className="label">تاريخ المناسبة:</span>
                <span className="value">{formatDate(booking.eventDate)}</span>
              </div>
            )}
            
            <div className="summary-item" style={{ marginTop: '20px' }}>
              <span className="label">المبلغ الإجمالي:</span>
              <span className="value">
                {totalAmount.toLocaleString('en-US')}
                <RiyalSymbol size={16} />
              </span>
            </div>
            
            <div className="summary-item promo-discount">
              <span className="label">المبلغ المدفوع سابقاً:</span>
              <span className="value discount-value">
                -{paidAmount.toLocaleString('en-US')}
                <RiyalSymbol size={16} />
              </span>
            </div>

            <div className="summary-item total" style={{ marginTop: '20px' }}>
              <span className="label">المبلغ المتبقي:</span>
              <span className="value">
                {remainingAmount.toLocaleString('en-US')}
                <RiyalSymbol size={20} />
              </span>
            </div>
          </div>
        </div>

        <div className="payment-form-container">
          <h2>معلومات الدفع</h2>
          {isProcessing ? (
            <div className="payment-loading-container">
              <div className="payment-loading-content">
                <div className="payment-spinner">
                  <div className="spinner-circle"></div>
                  <div className="spinner-circle-2"></div>
                  <div className="spinner-circle-3"></div>
                </div>
                <div className="loading-text">
                  <h3>جاري معالجة الدفع</h3>
                  <p>يرجى عدم إغلاق الصفحة أو الضغط على زر الرجوع</p>
                  <div className="loading-steps">
                    <div className="step active">
                      <div className="step-icon">💳</div>
                      <span>معالجة بيانات الدفع</span>
                    </div>
                    <div className="step">
                      <div className="step-icon">📧</div>
                      <span>تحديث حالة الحجز</span>
                    </div>
                    <div className="step">
                      <div className="step-icon">✅</div>
                      <span>إتمام العملية</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="payment-method-section">
                <h3>طريقة الدفع</h3>
                <div className="payment-method-options">
                  <div className="payment-method-option selected">
                    <div className="payment-option-wrapper">
                      <div className="payment-radio">
                        <div className="radio-circle geidea selected">
                          <div className="radio-dot"></div>
                        </div>
                      </div>
                      <div className="payment-method-content geidea-content">
                        <div className="geidea-text-wrapper">
                          <span className="payment-method-name">بوابة الدفع الآمنة</span>
                          <span className="payment-method-subtitle">بطاقة مدى فقط</span>
                        </div>
                        <img src="/images/payments.png" alt="وسائل الدفع" className="payment-methods-img" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="payment-notice">
                <p>سيتم توجيهك إلى بوابة الدفع الآمنة لإتمام عملية الدفع.</p>
              </div>
              
              <button 
                className="pay-now-btn"
                onClick={handlePayment}
                disabled={isProcessing}
              >
                {`دفع الآن - ${remainingAmount.toLocaleString('en-US')} ريال`}
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default PayRemaining;
